import { useEffect, useRef } from 'react';
import { isEmptyValue } from '../helpers';
import type {
  ExistingSubmissionFetcher,
  ExistingSubmissionQuery,
  FormSchema,
  FormStateValues,
} from '../types';

interface Args {
  schema: FormSchema;
  values: FormStateValues;
  setValues: (next: FormStateValues) => void;
  fetcher?: ExistingSubmissionFetcher;
  /**
   * Identifier values supplied by the calling context rather than by the
   * user's form input — e.g. workflow forms whose schema declares
   * `identifier: ["entity_id"]`. Keys here take precedence over matching
   * `values[title]` entries so the lookup can fire immediately on mount
   * without waiting for the user to type something into the form.
   */
  externalIdentifiers?: Record<string, string | number | undefined | null>;
  /** Debounce delay before issuing a lookup once all identifiers are filled. */
  debounceMs?: number;
}

/**
 * Read `schema.identifier` and prefill the rest of the form from
 * `existing_submission` once every identifier has a value.
 *
 * Identifier resolution per title (in order):
 *   1. `externalIdentifiers[title]` if non-empty — supplied by the calling
 *      modal (e.g. `entity_id` = the workflow's entityId).
 *   2. `values[title]` if non-empty — typed into the form by the user.
 *   3. Otherwise: skip until something fills.
 *
 * Behavior:
 *   - No-op when `fetcher` is not provided or `schema.identifier` is empty.
 *   - Debounces so the lookup doesn't fire on every keystroke.
 *   - Re-fires when any identifier value changes; skips when the combo
 *     matches the last applied one so the user's edits to prefilled fields
 *     aren't overwritten on every render.
 *   - Identifier fields themselves are never overwritten by the prefill.
 *   - Errors are swallowed — a missing record (or transient failure) just
 *     means the form stays empty.
 */
export const useExistingSubmissionPrefill = ({
  schema,
  values,
  setValues,
  fetcher,
  externalIdentifiers,
  debounceMs = 400,
}: Args): void => {
  const lastAppliedKeyRef = useRef<string | null>(null);
  const inflightRef = useRef<AbortController | null>(null);

  const identifierTitles = schema.identifier ?? [];

  // Build the query from current identifier values. Stringified so we can
  // detect "all filled" and use it as a memo key for the effect.
  const query: ExistingSubmissionQuery = {};
  let allFilled = identifierTitles.length > 0;
  for (const title of identifierTitles) {
    const ext = externalIdentifiers ? externalIdentifiers[title] : undefined;
    const raw = ext !== undefined && ext !== null && ext !== '' ? ext : values[title];
    if (isEmptyValue(raw)) {
      allFilled = false;
      break;
    }
    query[title] = String(raw).trim();
  }
  const queryKey = allFilled ? JSON.stringify(query) : '';

  useEffect(() => {
    if (!fetcher) return;
    if (!allFilled) return;
    if (queryKey === lastAppliedKeyRef.current) return;

    const timer = window.setTimeout(() => {
      inflightRef.current?.abort();
      const controller = new AbortController();
      inflightRef.current = controller;

      fetcher(query)
        .then((response) => {
          if (controller.signal.aborted) return;
          const record = response?.data?.list?.[0];
          if (!record || typeof record !== 'object') {
            lastAppliedKeyRef.current = queryKey;
            return;
          }
          // Only touch non-identifier fields so the user's typed identifier
          // values stay authoritative.
          const next: FormStateValues = {};
          const identifierSet = new Set(identifierTitles);
          for (const [k, v] of Object.entries(record)) {
            if (identifierSet.has(k)) continue;
            if (v === null || v === undefined) continue;
            next[k] = v;
          }
          if (Object.keys(next).length > 0) {
            setValues(next);
          }
          lastAppliedKeyRef.current = queryKey;
        })
        .catch(() => {
          // Swallow — prefill is best-effort.
          lastAppliedKeyRef.current = queryKey;
        });
    }, debounceMs);

    return () => {
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, allFilled, fetcher, debounceMs]);

  useEffect(() => {
    return () => {
      inflightRef.current?.abort();
    };
  }, []);
};
